import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button, Card, CardBody, CardHeader, Input, Label } from "@/Components/ui";
import {
  FrontTerminal,
  FrontTerminalPairingSession,
  FrontTerminalStatus,
  posService,
} from "@/api/services/pos.service";
import { dialogService } from "@/utils/dialogService";
import { toast } from "react-hot-toast";

const PAIRING_EXPIRY_OPTIONS = [300, 600, 900, 1800];

const statusLabelMap: Record<FrontTerminalStatus, string> = {
  ACTIVE: "활성",
  DISABLED: "비활성",
  REVOKED: "회수됨",
};

const statusButtonColorMap: Record<FrontTerminalStatus, "soft-success" | "soft-secondary" | "soft-danger"> = {
  ACTIVE: "soft-success",
  DISABLED: "soft-secondary",
  REVOKED: "soft-danger",
};

const formatDateTime = (value: string | null) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const getTerminalSummary = (terminal: FrontTerminal) => {
  if (terminal.merchant_name && terminal.merchant_id) {
    return `${terminal.merchant_name} (${terminal.merchant_id})`;
  }

  return terminal.merchant_name || terminal.merchant_id || "-";
};

const sortTerminals = (items: FrontTerminal[]) => {
  return [...items].sort((left, right) => {
    return left.label.localeCompare(right.label || left.serial_number) || left.id - right.id;
  });
};

type TerminalSettingsPanelProps = {
  initialTerminals?: FrontTerminal[];
  onTerminalsUpdated?: (terminals: FrontTerminal[]) => void;
};

export const TerminalSettingsPanel = ({
  initialTerminals = [],
  onTerminalsUpdated,
}: TerminalSettingsPanelProps) => {
  const [terminals, setTerminals] = useState<FrontTerminal[]>(sortTerminals(initialTerminals));
  const [loading, setLoading] = useState(false);
  const [issuingPairing, setIssuingPairing] = useState(false);
  const [terminalLabel, setTerminalLabel] = useState("");
  const [expiresInSeconds, setExpiresInSeconds] = useState(300);
  const [latestPairing, setLatestPairing] = useState<FrontTerminalPairingSession | null>(null);
  const [actioningTerminalId, setActioningTerminalId] = useState<number | null>(null);

  const publishTerminals = useCallback((nextTerminals: FrontTerminal[]) => {
    const sorted = sortTerminals(nextTerminals);
    setTerminals(sorted);
    onTerminalsUpdated?.(sorted);
  }, [onTerminalsUpdated]);

  useEffect(() => {
    setTerminals(sortTerminals(initialTerminals));
  }, [initialTerminals]);

  const loadTerminals = useCallback(async () => {
    setLoading(true);
    try {
      const response = await posService.listFrontTerminals();
      publishTerminals(response.data || []);
    } catch (error) {
      toast.error("단말 목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [publishTerminals]);

  useEffect(() => {
    void loadTerminals();
  }, [loadTerminals]);

  const activeCount = useMemo(
    () => terminals.filter((terminal) => terminal.status === "ACTIVE").length,
    [terminals],
  );

  const handleIssuePairing = useCallback(async () => {
    setIssuingPairing(true);
    try {
      const response = await posService.issueTerminalPairingSession({
        terminal_label: terminalLabel.trim(),
        expires_in_seconds: expiresInSeconds,
      });
      setLatestPairing(response.data);
      toast.success("페어링 코드가 발급되었습니다.");
    } catch (error) {
      toast.error("페어링 코드 발급에 실패했습니다.");
    } finally {
      setIssuingPairing(false);
    }
  }, [expiresInSeconds, terminalLabel]);

  const handleUpdateStatus = useCallback(
    async (terminal: FrontTerminal, nextStatus: Extract<FrontTerminalStatus, "DISABLED" | "REVOKED">) => {
      const actionLabel = nextStatus === "DISABLED" ? "비활성화" : "회수";
      const confirmed = await dialogService.confirm(`'${terminal.label || terminal.serial_number}' 단말을 ${actionLabel}하시겠습니까?`);
      if (!confirmed) {
        return;
      }

      setActioningTerminalId(terminal.id);
      try {
        await posService.updateFrontTerminal(terminal.id, { status: nextStatus });
        await loadTerminals();
        toast.success(`단말이 ${actionLabel}되었습니다.`);
      } catch (error) {
        toast.error(`단말 ${actionLabel}에 실패했습니다.`);
      } finally {
        setActioningTerminalId(null);
      }
    },
    [loadTerminals],
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card>
          <CardBody>
            <p className="text-muted mb-2">등록 단말</p>
            <h3 className="mb-0">{terminals.length}</h3>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-muted mb-2">활성 단말</p>
            <h3 className="mb-0">{activeCount}</h3>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-muted mb-2">최근 발급 페어링 코드</p>
            <h3 className="mb-0">{latestPairing ? latestPairing.pairing_code : "-"}</h3>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <h5 className="mb-1">페어링 코드 발급</h5>
            <p className="text-muted mb-0">플러그인 `settings.html`에서 이 코드를 입력해 단말을 현재 사이트에 연결합니다.</p>
          </div>
          <Button color="soft-primary" onClick={() => void loadTerminals()} disabled={loading}>
            새로고침
          </Button>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_180px_auto] md:items-end">
            <div>
              <Label className="form-label">단말 라벨</Label>
              <Input
                value={terminalLabel}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTerminalLabel(event.target.value)}
                placeholder="예: 카운터 1번"
              />
            </div>
            <div>
              <Label className="form-label">만료 시간</Label>
              <select
                className="form-select"
                value={String(expiresInSeconds)}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setExpiresInSeconds(Number(event.target.value))}
              >
                {PAIRING_EXPIRY_OPTIONS.map((seconds) => (
                  <option key={seconds} value={seconds}>
                    {Math.floor(seconds / 60)}분
                  </option>
                ))}
              </select>
            </div>
            <Button color="primary" onClick={handleIssuePairing} disabled={issuingPairing}>
              {issuingPairing ? "발급 중..." : "페어링 코드 발급"}
            </Button>
          </div>

          {latestPairing ? (
            <div className="mt-4 rounded-3 border border-slate-200 bg-slate-50 p-4">
              <p className="text-muted mb-1">가장 최근 발급 코드</p>
              <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                <div>
                  <h2 className="mb-1 tracking-[0.4em] font-mono">{latestPairing.pairing_code}</h2>
                  <p className="mb-0 text-muted">
                    라벨: {latestPairing.terminal_label || "미지정"} · 만료: {formatDateTime(latestPairing.expires_at)}
                  </p>
                </div>
                <p className="mb-0 text-sm text-slate-500">코드는 한 번만 사용되며 만료 후 자동 무효화됩니다.</p>
              </div>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h5 className="mb-1">등록 단말 목록</h5>
          <p className="text-muted mb-0">heartbeat 와 poll 시각을 기준으로 단말 상태를 확인하고 비활성화 또는 회수할 수 있습니다.</p>
        </CardHeader>
        <CardBody>
          {loading ? (
            <p className="text-muted mb-0">불러오는 중...</p>
          ) : (
            <div className="w-full overflow-x-auto">
              <table className="w-full mb-0 text-sm border-y border-slate-200">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                  <tr>
                    <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">라벨</th>
                    <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">시리얼</th>
                    <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">가맹점</th>
                    <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">상태</th>
                    <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">토큰 힌트</th>
                    <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">최근 접속</th>
                    <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">최근 poll</th>
                    <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">페어링 시각</th>
                    <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">관리</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {terminals.map((terminal) => (
                    <tr key={terminal.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="p-4 align-middle whitespace-nowrap text-slate-700">{terminal.label || "미지정"}</td>
                      <td className="p-4 align-middle whitespace-nowrap text-slate-700">{terminal.serial_number}</td>
                      <td className="p-4 align-middle whitespace-nowrap text-slate-700">{getTerminalSummary(terminal)}</td>
                      <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                        <Button color={statusButtonColorMap[terminal.status]} size="sm" disabled>
                          {statusLabelMap[terminal.status]}
                        </Button>
                      </td>
                      <td className="p-4 align-middle whitespace-nowrap text-slate-700">{terminal.auth_token_hint || "-"}</td>
                      <td className="p-4 align-middle whitespace-nowrap text-slate-700">{formatDateTime(terminal.last_seen_at)}</td>
                      <td className="p-4 align-middle whitespace-nowrap text-slate-700">{formatDateTime(terminal.last_polled_at)}</td>
                      <td className="p-4 align-middle whitespace-nowrap text-slate-700">{formatDateTime(terminal.paired_at)}</td>
                      <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                        <div className="flex flex-wrap gap-2">
                          {terminal.status === "ACTIVE" ? (
                            <>
                              <Button
                                color="soft-secondary"
                                size="sm"
                                disabled={actioningTerminalId === terminal.id}
                                onClick={() => void handleUpdateStatus(terminal, "DISABLED")}
                              >
                                비활성화
                              </Button>
                              <Button
                                color="soft-danger"
                                size="sm"
                                disabled={actioningTerminalId === terminal.id}
                                onClick={() => void handleUpdateStatus(terminal, "REVOKED")}
                              >
                                회수
                              </Button>
                            </>
                          ) : (
                            <span className="text-muted">재페어링으로 다시 활성화할 수 있습니다.</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {terminals.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-8">등록된 단말이 없습니다.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default TerminalSettingsPanel;
